import { createFileRoute, Link } from "@tanstack/react-router";
import { useLingui } from "@lingui/react/macro";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useMaterial } from "@/hooks/useMaterials";
import { Skeleton } from "@/components/ui/skeleton";
import { canEditMaterial } from "@/lib/materials/shared-catalogue";
import { MaterialDetailHeader, MaterialDetailView } from "@/components/materials/detail";
import { CreateMaterialDialog } from "@/components/recipes/dialogs/CreateMaterialDialog";

export const Route = createFileRoute("/(protected)/_layout/materials/$materialId")({
  component: MaterialDetailPage,
});

function MaterialDetailPage() {
  const { t } = useLingui();
  const { materialId } = Route.useParams();
  const id = Number(materialId);
  const { isAuthenticated, hasPermission } = useAuth();
  const isSuperAdmin = hasPermission("*:*");
  const { data: material, isLoading, error } = useMaterial(id, isAuthenticated);
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-5 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Link
          to="/materials"
          search={{ page: 1, pageSize: 50, ingredientStatus: "all" }}
          className="inline-flex items-center gap-1.5 text-sm text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
        >
          <ArrowLeft className="size-4" />
          {t`Back to Materials`}
        </Link>
        <p className="text-(--color-text-secondary)">{t`This ingredient couldn't be found.`}</p>
      </div>
    );
  }

  const canEdit = canEditMaterial(material, isSuperAdmin);

  return (
    <div className="flex flex-1 flex-col gap-5 py-5">
      <MaterialDetailHeader material={material} canEdit={canEdit} onEdit={() => setEditOpen(true)} />
      <MaterialDetailView material={material} canEdit={canEdit} onEdit={() => setEditOpen(true)} />
      <CreateMaterialDialog open={editOpen} onOpenChange={setEditOpen} editMaterial={material} />
    </div>
  );
}
